import { useSyncExternalStore } from "react";
import api, { apiFormData } from "../../../utils/api";
import { fetchAgents } from "./agentsService";
import { unwrap } from "./apiEnvelope";

// ============================================================================
// CRM Chat — omni-channel conversations store (WhatsApp / Messenger / Instagram).
//
// Powers /crm/tareas/chat: a real conversations inbox where a human agent — or
// the AI — talks to clients over Meta's messaging channels. This used to be a
// localStorage mock; it is now backed by the torre-de-control-api `crmMessaging`
// module (crm_conversations + crm_messages). The transport is Meta Cloud API:
// inbound messages arrive via the webhook and are persisted server-side; the UI
// polls for fresh state (no socket server in this stack).
//
//   list      : GET  /crm/conversations
//   thread    : GET  /crm/conversations/messages?conversationId=
//   send      : POST /crm/conversations/send        (human → pauses the AI)
//   assign    : POST /crm/conversations/assign
//   ai-toggle : POST /crm/conversations/ai-toggle    (per-conversation AI on/off)
//   handoff   : POST /crm/conversations/handoff-resolve (mark an AI handoff attended)
//   read      : POST /crm/conversations/read
//   perms     : GET  /crm/messaging/permissions      (role-based chat permissions, #524)
//   config    : GET/PUT /crm/chat/config             (welcome menu on first contact, #1525)
// ============================================================================

export type Channel = "whatsapp" | "messenger" | "instagram";
export type Direction = "in" | "out";
export type MessageStatus = "sent" | "delivered" | "read" | "failed";
export type AuthorType = "lead" | "agent" | "ai";

export interface Conversation {
  id: string;
  leadId: string | null;
  channel: Channel;
  externalId: string;
  contactName: string | null;
  lastMessageAt: number | null;
  lastInboundAt: number | null;
  lastMessageText: string | null;
  lastDirection: Direction | null;
  unreadCount: number;
  assignedAgentId: string | null;
  aiEnabled: boolean;
  status: string;
  /** WhatsApp 24h window: free-text allowed only when open (else send a template). */
  windowOpen: boolean;
  /** Why the AI paused itself (chat-agent guardrails); null/absent when a person turned it off or it is on. */
  aiPausedReason?: AiPausedReason | null;
  aiPausedAt?: number | null;
  /** The AI handed this conversation to a person and nobody has attended it yet. */
  needsHumanAttention?: boolean;
  handoffAt?: number | null;
  handoffReason?: HandoffReason | null;
  /** Summary the AI (or the backup rule) left for the agent. */
  handoffSummary?: string | null;
  /** "model" = Claude called escalar_a_humano; "rule" = deterministic backup trigger. */
  handoffSource?: "model" | "rule" | null;
}

/** Automatic AI pause reasons set by the backend (guardrails + human handoff). */
export type AiPausedReason = "rate_limit" | "loop_suspected" | "handoff";

/** Why the AI handed a conversation to a person (backend chatAgent/handoff.js). */
export type HandoffReason =
  | "pide_humano"
  | "queja"
  | "tema_legal"
  | "tema_pago"
  | "enojo"
  | "sin_avance"
  | "fuera_de_alcance"
  | "otro";

export const HANDOFF_REASON_LABELS: Record<HandoffReason, string> = {
  pide_humano: "Pidió hablar con una persona",
  queja: "Queja o reclamo",
  tema_legal: "Tema legal",
  tema_pago: "Pagos, cobros o reembolsos",
  enojo: "Cliente molesto",
  sin_avance: "La conversación no avanza",
  fuera_de_alcance: "Fuera del alcance de la IA",
  otro: "Otro motivo",
};

export const AI_PAUSED_REASON_LABELS: Record<AiPausedReason, string> = {
  rate_limit: "Se alcanzó el tope de respuestas automáticas",
  loop_suspected: "Parece un contestador automático (posible bucle)",
  handoff: "Transferida a un asesor humano",
};

/** Does this conversation need a person? (handoff not yet attended) */
export function needsHuman(c: Pick<Conversation, "needsHumanAttention" | "handoffAt">): boolean {
  return Boolean(c.needsHumanAttention ?? c.handoffAt);
}

const CLEARED_HANDOFF: Partial<Conversation> = {
  needsHumanAttention: false,
  handoffAt: null,
  handoffReason: null,
  handoffSummary: null,
  handoffSource: null,
};

export interface ChatMessage {
  id: string;
  conversationId: string;
  leadId: string | null;
  channel: Channel;
  direction: Direction;
  text: string;
  mediaUrl: string | null;
  mediaType: string | null;
  authorType: AuthorType | null;
  authorId: string | null;
  status: MessageStatus | null;
  isTemplate?: boolean;
  templateName?: string | null;
  /** Actions the AI ran for this reply (book a demo, update the lead…). */
  aiToolCalls?: AiToolCall[];
  /** "interactive" cuando la burbuja fue una lista/botón de WhatsApp (#1525); su texto es el menú renderizado. */
  kind?: "interactive" | null;
  /** Opción del menú de bienvenida que eligió la persona (mensajes entrantes). */
  menuOptionId?: string | null;
  at: number;
}

/** One agent action recorded on an AI message (backend chatAgent/actions.js). */
export interface AiToolCall {
  tool: "consultar_disponibilidad" | "agendar_demo" | "actualizar_lead" | string;
  ok: boolean;
  /** Spanish, ready to show: «Agendó demo · 15 sep 11:00». */
  label: string;
  error: string | null;
  input?: Record<string, unknown>;
  bookingId?: string;
  fields?: string[];
  at?: number | null;
}

/** One component of an approved template (Graph API shape: BODY/HEADER/FOOTER/BUTTONS). */
export interface WhatsAppTemplateComponent {
  type: string;
  text?: string;
  format?: string;
}

/** An approved WhatsApp message template (for the picker). */
export interface WhatsAppTemplate {
  name: string;
  language: string;
  category: string | null;
  components?: WhatsAppTemplateComponent[];
}

/** A component payload when SENDING a template (Graph API shape). */
export interface TemplateSendComponent {
  type: string;
  parameters: Array<{ type: string; text?: string }>;
}

/** Per-channel presentation (icon / label / accent). */
export const CHANNEL_META: Record<Channel, { label: string; icon: string; color: string; bg: string }> = {
  whatsapp: { label: "WhatsApp", icon: "ri-whatsapp-line", color: "text-emerald-500", bg: "bg-emerald-500" },
  messenger: { label: "Messenger", icon: "ri-messenger-line", color: "text-blue-500", bg: "bg-blue-500" },
  instagram: { label: "Instagram", icon: "ri-instagram-line", color: "text-pink-500", bg: "bg-pink-500" },
};

export const CHANNELS: Channel[] = ["whatsapp", "messenger", "instagram"];

// --- Agents (real human reps) ----------------------------------------------
// Agents are the sales reps that can own / act on a conversation. They are the
// CRM "human" agents (Agentes), fetched live from the backend — the same roster
// the rest of the CRM (Reuniones, lead ownership, etc.) uses — so the chat
// "Actuando como" and "Asignar" dropdowns never drift from the real list.
// The agent record carries no avatar, so we derive a stable initials + color.

/** A sales rep shown in the chat dropdowns. */
export interface Agent {
  id: string;
  name: string;
  initials: string;
  color: string; // avatar bg
}

const AGENT_COLORS = [
  "bg-rose-500", "bg-sky-500", "bg-violet-500", "bg-amber-500",
  "bg-emerald-500", "bg-indigo-500", "bg-teal-500", "bg-pink-500",
];

function agentInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase() || "?";
}

/** Deterministic color from the id, so an agent always gets the same avatar. */
function agentColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AGENT_COLORS[h % AGENT_COLORS.length];
}

let agents: Agent[] = [];
let agentsLoaded = false;
let agentsLoading = false;
const agentListeners = new Set<() => void>();

async function loadAgents(): Promise<void> {
  if (agentsLoading) return;
  agentsLoading = true;
  try {
    const page = await fetchAgents({ type: "human", limit: 300 });
    agents = page.data.map((a) => ({
      id: a.id,
      name: a.name,
      initials: agentInitials(a.name),
      color: agentColor(a.id),
    }));
    agentsLoaded = true;
    agentListeners.forEach((l) => l());
  } catch {
    /* leave the roster empty; the dropdowns just show no options until a retry */
  } finally {
    agentsLoading = false;
  }
}

function subscribeAgents(listener: () => void): () => void {
  agentListeners.add(listener);
  if (!agentsLoaded) void loadAgents();
  return () => {
    agentListeners.delete(listener);
  };
}

/** Live roster of human agents (sales reps) for the chat dropdowns. */
export function useAgents(): Agent[] {
  return useSyncExternalStore(subscribeAgents, () => agents, () => agents);
}

// --- Permissions (#524) -------------------------------------------------------
// The backend is the source of truth (every crmMessaging route checks a chat
// permission and answers 403 otherwise). The UI fetches the caller's effective
// permissions once and hides/disables what they can't do. Until they load,
// every action is treated as NOT allowed (fail-closed).

export type ChatPermission = "read" | "reply" | "template" | "broadcast" | "assign" | "ai_toggle";

export interface ChatPermissionsState {
  loaded: boolean;
  isAdmin: boolean;
  permissions: Record<ChatPermission, boolean>;
}

const NO_CHAT_PERMISSIONS: Record<ChatPermission, boolean> = {
  read: false,
  reply: false,
  template: false,
  broadcast: false,
  assign: false,
  ai_toggle: false,
};

let chatPermissions: ChatPermissionsState = { loaded: false, isAdmin: false, permissions: NO_CHAT_PERMISSIONS };
let chatPermissionsLoading = false;
const permissionListeners = new Set<() => void>();

async function loadChatPermissions(): Promise<void> {
  if (chatPermissionsLoading) return;
  chatPermissionsLoading = true;
  try {
    const res = await api.get("/crm/messaging/permissions");
    const data = res.data?.data ?? {};
    chatPermissions = {
      loaded: true,
      isAdmin: Boolean(data.isAdmin),
      permissions: { ...NO_CHAT_PERMISSIONS, ...(data.permissions ?? {}) },
    };
  } catch {
    chatPermissions = { loaded: true, isAdmin: false, permissions: NO_CHAT_PERMISSIONS };
  } finally {
    chatPermissionsLoading = false;
    permissionListeners.forEach((l) => l());
  }
}

function subscribeChatPermissions(listener: () => void): () => void {
  permissionListeners.add(listener);
  // Re-fetch on every (first) subscriber so a role change applies on reopening the inbox.
  if (permissionListeners.size === 1) void loadChatPermissions();
  return () => {
    permissionListeners.delete(listener);
  };
}

/** Effective chat permissions of the logged-in user. */
export function useChatPermissions(): ChatPermissionsState {
  return useSyncExternalStore(subscribeChatPermissions, () => chatPermissions, () => chatPermissions);
}

// --- Store ------------------------------------------------------------------

interface ChatState {
  conversations: Conversation[];
  messages: Record<string, ChatMessage[]>; // conversationId -> thread (asc by at)
  loadingList: boolean;
  selectedId: string | null;
}

let state: ChatState = { conversations: [], messages: {}, loadingList: true, selectedId: null };
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}
function setState(patch: Partial<ChatState>) {
  state = { ...state, ...patch };
  emit();
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function getSnapshot(): ChatState {
  return state;
}
export function useChatStore(): ChatState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function patchConversation(id: string, patch: Partial<Conversation>) {
  setState({
    conversations: state.conversations.map((c) => (c.id === id ? { ...c, ...patch } : c)),
  });
}

// --- API ---------------------------------------------------------------------

interface ListParams {
  channel?: Channel;
  unread?: boolean;
  assignedAgentId?: string;
  limit?: number;
}

async function apiListConversations(params: ListParams = {}): Promise<Conversation[]> {
  const res = await api.get("/crm/conversations", {
    params: {
      limit: params.limit ?? 100,
      ...(params.channel ? { channel: params.channel } : {}),
      ...(params.unread ? { unread: true } : {}),
      ...(params.assignedAgentId ? { assignedAgentId: params.assignedAgentId } : {}),
    },
  });
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

async function apiFetchMessages(conversationId: string): Promise<ChatMessage[]> {
  const res = await api.get("/crm/conversations/messages", { params: { conversationId } });
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

// --- Actions -----------------------------------------------------------------

/** Refresh the conversation list (called on mount + on a poll interval). */
export async function refreshConversations(params: ListParams = {}): Promise<void> {
  try {
    const conversations = await apiListConversations(params);
    setState({ conversations, loadingList: false });
  } catch {
    setState({ loadingList: false });
  }
}

/** Refresh a single thread (called when opened + on a poll interval). */
export async function refreshMessages(conversationId: string): Promise<void> {
  if (!conversationId) return;
  try {
    const msgs = await apiFetchMessages(conversationId);
    setState({ messages: { ...state.messages, [conversationId]: msgs } });
  } catch {
    /* keep what we have */
  }
}

export function selectConversation(id: string | null): void {
  setState({ selectedId: id });
  if (id) {
    refreshMessages(id);
    markRead(id);
  }
}

/** Agent sends an outbound message (optimistic). A human send pauses the AI. */
export async function sendMessage(conversationId: string, text: string, agentId?: string): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed || !conversationId) return;
  const conv = state.conversations.find((c) => c.id === conversationId);

  // Optimistic bubble so the composer feels instant.
  const optimistic: ChatMessage = {
    id: `optim-${conversationId}-${state.messages[conversationId]?.length ?? 0}-${Math.round(performance.now())}`,
    conversationId,
    leadId: conv?.leadId ?? null,
    channel: conv?.channel ?? "whatsapp",
    direction: "out",
    text: trimmed,
    mediaUrl: null,
    mediaType: null,
    authorType: "agent",
    authorId: agentId ?? conv?.assignedAgentId ?? null,
    status: "sent",
    at: Date.now(),
  };
  setState({
    messages: {
      ...state.messages,
      [conversationId]: [...(state.messages[conversationId] ?? []), optimistic],
    },
  });
  // A human reply takes over the thread → reflect the AI pause immediately.
  patchConversation(conversationId, { aiEnabled: false });

  try {
    await api.post("/crm/conversations/send", { conversationId, text: trimmed });
  } catch {
    /* surfaced by the reconcile below (failed sends get a failed status) */
  } finally {
    await refreshMessages(conversationId);
    await refreshConversations();
  }
}

/** Turn the AI on/off for a conversation. Turning it on answers any pending message. */
export async function setAiEnabled(conversationId: string, enabled: boolean): Promise<void> {
  // Turning the AI back on clears any pause reason and a pending human handoff.
  patchConversation(conversationId, enabled ? { aiEnabled: true, aiPausedReason: null, ...CLEARED_HANDOFF } : { aiEnabled: false });
  try {
    await api.post("/crm/conversations/ai-toggle", { conversationId, enabled });
  } finally {
    await refreshConversations();
    if (enabled) await refreshMessages(conversationId);
  }
}

/** Mark a conversation the AI handed off as attended (the AI stays off). */
export async function resolveHandoff(conversationId: string): Promise<void> {
  patchConversation(conversationId, CLEARED_HANDOFF);
  try {
    await api.post("/crm/conversations/handoff-resolve", { conversationId });
  } finally {
    await refreshConversations();
  }
}

/** Assign (or reassign) a conversation to an agent. */
export async function assignConversation(conversationId: string, agentId: string | undefined): Promise<void> {
  patchConversation(conversationId, { assignedAgentId: agentId ?? null });
  try {
    await api.post("/crm/conversations/assign", { conversationId, agentId: agentId ?? null });
  } finally {
    await refreshConversations();
  }
}

/** Clear the unread counter for a conversation. */
export async function markRead(conversationId: string): Promise<void> {
  const conv = state.conversations.find((c) => c.id === conversationId);
  if (!conv || conv.unreadCount === 0) return;
  patchConversation(conversationId, { unreadCount: 0 });
  try {
    await api.post("/crm/conversations/read", { conversationId });
  } catch {
    /* best effort */
  }
}

/** Agent sends an attachment (image/file) with an optional caption. */
export async function sendMedia(conversationId: string, file: File, text?: string): Promise<void> {
  if (!conversationId || !file) return;
  const form = new FormData();
  form.append("conversationId", conversationId);
  if (text && text.trim()) form.append("text", text.trim());
  form.append("file", file);
  patchConversation(conversationId, { aiEnabled: false }); // human send pauses AI
  try {
    await apiFormData.post("/crm/conversations/send-media", form);
  } finally {
    await refreshMessages(conversationId);
    await refreshConversations();
  }
}

/** Fetch the approved WhatsApp templates (for the out-of-window picker). */
export async function fetchTemplates(): Promise<WhatsAppTemplate[]> {
  try {
    const res = await api.get("/crm/messaging/templates");
    return Array.isArray(res.data?.data) ? res.data.data : [];
  } catch {
    return [];
  }
}

// --- Messaging status (Configuración → Integraciones) ------------------------

/** Where a credential comes from: env var, stored in crm_integrations, or absent. */
export type CredentialSource = "env" | "stored" | null;

/** GET /crm/messaging/status — presence + source per credential, never values. */
export interface MessagingStatus {
  configured: boolean;
  graphVersion: string;
  /** Path appended to the API base to form the webhook URL to paste into Meta. */
  webhookPath: string;
  appSecretSet: boolean;
  verifyTokenSet: boolean;
  templatesConfigured: boolean;
  sources: {
    appSecret: CredentialSource;
    verifyToken: CredentialSource;
    whatsappToken: CredentialSource;
    whatsappPhoneNumberId: CredentialSource;
    whatsappBusinessAccountId: CredentialSource;
  };
  channels: {
    whatsapp: { canSend: boolean; phoneNumberId: boolean };
    messenger: { canSend: boolean };
    instagram: { canSend: boolean };
  };
}

/** Fetch the Meta messaging connection status (for the integrations panel). */
export async function fetchMessagingStatus(): Promise<MessagingStatus> {
  const res = await api.get("/crm/messaging/status");
  return res.data?.data as MessagingStatus;
}

// --- Menú de bienvenida (#1525, por producto #1536) ---------------------------
// GET/PUT /crm/chat/config → crm_chat_config/default. El menú se manda solo al
// primer mensaje de cada conversación (lista interactiva en WhatsApp; texto
// numerado en Messenger/Instagram). Hay UNO POR PRODUCTO (`welcomeMenus`:
// Wayak B2B, Movik B2C) y `accountProducts` dice qué menú usa cada número o
// página; un número sin entrada usa el de Wayak. `welcomeMenu` (un solo menú)
// sigue llegando y equivale al de Wayak. Los límites son los de la lista de
// WhatsApp; la API los vuelve a validar y responde 422 con meta.code.

export type WelcomeMenuAction = "link" | "tool";

export interface WelcomeMenuOption {
  /** Minúsculas, letras/números/-/_ (es el id de la fila en WhatsApp). */
  id: string;
  title: string;
  description: string;
  action: WelcomeMenuAction;
  /** Sólo `link`: https://… (la API le agrega utm_source=whatsapp&utm_medium=chatbot). */
  url?: string;
  /** Sólo `tool`: la acción del agente que la resuelve. */
  tool?: string;
  /** Sólo `link`: código del enlace corto; viaja como `ref=<código>` en la URL. */
  shortCode?: string;
  /** Sólo `tool: escalar_a_humano`: texto fijo que se manda al pasar con una persona (≤1024). */
  replyText?: string;
}

export interface WelcomeMenu {
  enabled: boolean;
  title: string;
  body: string;
  buttonText: string;
  options: WelcomeMenuOption[];
}

/** Productos con menú propio (mismos que chatConfigService.MENU_PRODUCTS de la API). */
export type MenuProduct = "Wayak" | "Movik";
export const MENU_PRODUCTS: { value: MenuProduct; label: string }[] = [
  { value: "Wayak", label: "Wayak Logistics (B2B)" },
  { value: "Movik", label: "Movik (B2C)" },
];
export const DEFAULT_MENU_PRODUCT: MenuProduct = "Wayak";

/** Un número de WhatsApp, página o cuenta de Instagram conectado a la bandeja. */
export interface ChannelAccount {
  id: string;
  channel: Channel | null;
  label: string | null;
  displayPhoneNumber: string | null;
}

export interface ChatConfig {
  /** El de Wayak (compatibilidad con el documento de un solo menú). */
  welcomeMenu: WelcomeMenu;
  welcomeMenus: Record<MenuProduct, WelcomeMenu>;
  /** `{ channelAccountId: producto }` — phone_number_id de WhatsApp, id de página o de Instagram. */
  accountProducts: Record<string, MenuProduct>;
  /** Los números/páginas que se pueden asignar (sólo existentes; la API rechaza otros). */
  channelAccounts: ChannelAccount[];
}

/** Lo que se manda al guardar: los menús que se cambian y, si viene, el mapa entero de números. */
export interface ChatConfigInput {
  welcomeMenu?: WelcomeMenu;
  welcomeMenus?: Partial<Record<MenuProduct, WelcomeMenu>>;
  accountProducts?: Record<string, MenuProduct>;
}

/** Límites de la lista de WhatsApp (los mismos que valida la API). */
export const WELCOME_MENU_LIMITS = {
  title: 60,
  body: 1024,
  buttonText: 20,
  options: 10,
  optionTitle: 24,
  optionDescription: 72,
  replyText: 1024,
} as const;

/** Lo que manda «pasar con una persona» si la opción no trae su propio texto (igual que la API). */
export const DEFAULT_HANDOFF_REPLY = "Te comunico con una persona del equipo. En breve te contesta por aquí.";

/** Etiqueta legible de un número/página de la bandeja. */
export function channelAccountLabel(account: ChannelAccount): string {
  const channel = account.channel === "messenger" ? "Messenger" : account.channel === "instagram" ? "Instagram" : "WhatsApp";
  const name = account.label || account.displayPhoneNumber;
  return name ? `${channel} · ${name} (${account.id})` : `${channel} · ${account.id}`;
}

/** Acciones del agente a las que puede apuntar una opción `tool`. */
export const WELCOME_MENU_TOOLS: { id: string; label: string }[] = [
  { id: "agendar_demo", label: "Agendar demo (IA)" },
  { id: "escalar_a_humano", label: "Pasar con una persona (soporte)" },
];

/** Las cinco opciones del funnel de WAYAK (ids estables, iguales a los de la landing). */
export const WAYAK_WELCOME_MENU_PRESET: WelcomeMenu = {
  enabled: true,
  title: "¡Hola! Soy el asistente de WAYAK 👋",
  body: "Cuéntanos qué necesitas y te llevamos directo. Elige una opción:",
  buttonText: "Ver opciones",
  options: [
    { id: "demo", title: "Agendar demo", description: "Te mostramos WAYAK en una videollamada corta", action: "tool", tool: "agendar_demo" },
    { id: "piloto", title: "Prueba piloto", description: "Prueba WAYAK en tus unidades sin compromiso", action: "link", url: "https://wayak-logistics.com/#/prueba-piloto" },
    { id: "cuenta-gratis", title: "Crear cuenta gratis", description: "Empieza hoy con el plan gratuito", action: "link", url: "https://wayak-logistics.com/#/prueba" },
    { id: "comprar", title: "Comprar dispositivo", description: "GPS y accesorios en la tienda en línea", action: "link", url: "https://app.wayak-logistics.com/shop/wayak" },
    { id: "vincular", title: "Vincular dispositivo", description: "Ya tienes un GPS: conéctalo a tu cuenta", action: "link", url: "https://app.wayak-logistics.com/dispositivos/vincular" },
  ],
};

/** Movik (B2C, #1536): descargar la app, cómo funciona el acompañamiento y soporte con una persona. */
export const MOVIK_WELCOME_MENU_PRESET: WelcomeMenu = {
  enabled: true,
  title: "¡Hola! Soy el asistente de Movik 👋",
  body: "Te acompañamos en cada trayecto. Elige una opción:",
  buttonText: "Ver opciones",
  options: [
    { id: "descargar", title: "Descargar Movik", description: "Abre la app y crea tu cuenta en minutos", action: "link", url: "https://movik-production.web.app/" },
    { id: "como-funciona", title: "¿Cómo funciona?", description: "Así funciona el acompañamiento de tus trayectos", action: "link", url: "https://movik-production.web.app/" },
    { id: "soporte", title: "Hablar con soporte", description: "Te atiende una persona del equipo", action: "tool", tool: "escalar_a_humano" },
  ],
};

export const WELCOME_MENU_PRESETS: Record<MenuProduct, WelcomeMenu> = {
  Wayak: WAYAK_WELCOME_MENU_PRESET,
  Movik: MOVIK_WELCOME_MENU_PRESET,
};

const emptyMenuFor = (product: MenuProduct): WelcomeMenu => ({
  enabled: false,
  title: WELCOME_MENU_PRESETS[product].title,
  body: WELCOME_MENU_PRESETS[product].body,
  buttonText: WELCOME_MENU_PRESETS[product].buttonText,
  options: [],
});

export const EMPTY_WELCOME_MENU: WelcomeMenu = emptyMenuFor("Wayak");

function menuOr(product: MenuProduct, menu: Partial<WelcomeMenu> | null | undefined): WelcomeMenu {
  return { ...emptyMenuFor(product), ...(menu ?? {}), options: Array.isArray(menu?.options) ? menu!.options : [] };
}

function unwrapConfig(res: unknown): ChatConfig {
  const value = unwrap<Partial<ChatConfig>>(res);
  const stored = (value?.welcomeMenus ?? {}) as Partial<Record<MenuProduct, WelcomeMenu>>;
  // Sin `welcomeMenus` (documento o API anteriores) el menú único es el de Wayak.
  const welcomeMenus: Record<MenuProduct, WelcomeMenu> = {
    Wayak: menuOr("Wayak", stored.Wayak ?? value?.welcomeMenu),
    Movik: menuOr("Movik", stored.Movik),
  };
  const accountProducts: Record<string, MenuProduct> = Object.create(null);
  for (const [id, product] of Object.entries(value?.accountProducts ?? {})) {
    if (MENU_PRODUCTS.some((p) => p.value === product)) accountProducts[id] = product as MenuProduct;
  }
  const channelAccounts: ChannelAccount[] = (Array.isArray(value?.channelAccounts) ? value!.channelAccounts : [])
    .filter((a): a is ChannelAccount => Boolean(a && typeof a.id === "string" && a.id))
    .map((a) => ({ id: a.id, channel: a.channel ?? null, label: a.label ?? null, displayPhoneNumber: a.displayPhoneNumber ?? null }));
  return { welcomeMenu: welcomeMenus.Wayak, welcomeMenus, accountProducts, channelAccounts };
}

/** GET /crm/chat/config — lee sin caché (la ingesta sí cachea 60 s). */
export async function fetchChatConfig(signal?: AbortSignal): Promise<ChatConfig> {
  return unwrapConfig(await api.get("/crm/chat/config", { signal }));
}

/**
 * PUT /crm/chat/config — cada menú enviado se guarda completo; los productos
 * que no se envían se conservan. `accountProducts`, si viene, reemplaza el mapa.
 * Sólo administradores del chat.
 */
export async function saveChatConfig(config: ChatConfigInput): Promise<ChatConfig> {
  return unwrapConfig(await api.put("/crm/chat/config", config));
}

type ApiError = { response?: { status?: number; data?: { meta?: { code?: unknown; message?: unknown; details?: { accountId?: unknown } } } } };

/** Mensaje ya traducido por la API (meta.message) y su código estable, o uno genérico. */
export function chatConfigErrorMessage(error: unknown, fallback = "No se pudo guardar la configuración del chat."): string {
  const meta = (error as ApiError)?.response?.data?.meta;
  const message = typeof meta?.message === "string" && meta.message.trim() ? meta.message : fallback;
  const accountId = typeof meta?.details?.accountId === "string" ? meta.details.accountId : "";
  const withAccount = accountId ? `${message} Número: ${accountId}.` : message;
  return typeof meta?.code === "string" && meta.code ? `${withAccount} (${meta.code})` : withAccount;
}

/** Text of the template's BODY component ("" when it has none). */
export function templateBodyText(tpl: WhatsAppTemplate): string {
  const comps = Array.isArray(tpl.components) ? tpl.components : [];
  const body = comps.find((c) => String(c?.type).toUpperCase() === "BODY");
  return body?.text || "";
}

/** How many distinct {{n}} placeholders the template's BODY expects. */
export function templateBodyParams(tpl: WhatsAppTemplate): number {
  const seen = new Set<number>();
  for (const m of templateBodyText(tpl).matchAll(/\{\{(\d+)\}\}/g)) {
    seen.add(Number(m[1]));
  }
  return seen.size;
}

/** Send a WhatsApp template (used to (re)open a conversation outside the 24h window). */
export async function sendTemplate(
  conversationId: string,
  templateName: string,
  languageCode = "es",
  preview?: string,
  components?: TemplateSendComponent[],
): Promise<void> {
  if (!conversationId || !templateName) return;
  patchConversation(conversationId, { aiEnabled: false });
  try {
    await api.post("/crm/conversations/send-template", {
      conversationId,
      templateName,
      languageCode,
      ...(preview ? { preview } : {}),
      ...(components && components.length ? { components } : {}),
    });
  } finally {
    await refreshMessages(conversationId);
    await refreshConversations();
  }
}

export interface BroadcastInput {
  scope: "all" | "channel" | "selected";
  channel?: Channel;
  conversationIds?: string[];
  text?: string;
  template?: { name: string; languageCode?: string; preview?: string; components?: TemplateSendComponent[] };
}

export interface BroadcastResult {
  broadcastId: string;
  queued: number;
  skipped: number;
  targets: number;
}

/** Broadcast a message/template to many conversations (enqueued + throttled). */
export async function broadcast(input: BroadcastInput): Promise<BroadcastResult | null> {
  try {
    const res = await api.post("/crm/messaging/broadcast", input);
    await refreshConversations();
    return res.data?.data ?? null;
  } catch {
    return null;
  }
}

// --- Formatting helpers -----------------------------------------------------

export function msgTime(at: number): string {
  return new Date(at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

export function relTime(at: number): string {
  const min = Math.floor((Date.now() - at) / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} d`;
}

/** Day label for the in-thread date separators. */
export function dayLabel(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const yest = new Date(today.getTime() - 86400000);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Hoy";
  if (sameDay(d, yest)) return "Ayer";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long" });
}

/** Normalize a WhatsApp-style id/number to digits for a wa.me deep link. */
export function waDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/[^\d]/g, "");
}
